import { createContext, useContext, useEffect, useState, useCallback } from "react";

const AuthContext = createContext(null);
const STORAGE_KEY = "wow_auth_user";
const USERS_KEY = "wow_users";

function readUsers() {
  try {
    const raw = localStorage.getItem(USERS_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

function writeUsers(users) {
  localStorage.setItem(USERS_KEY, JSON.stringify(users));
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  });

  useEffect(() => {
    if (user) localStorage.setItem(STORAGE_KEY, JSON.stringify(user));
    else localStorage.removeItem(STORAGE_KEY);
  }, [user]);

  const login = useCallback(({ username, password, role = "customer" }) => {
    const u = (username || "").trim();
    const p = (password || "").trim();
    if (!u || !p) return { ok: false, error: "Enter username and password." };

    // Universal test credential
    if (u === "test" && p === "test") {
      const profile = {
        username: "test",
        name: role === "admin" ? "Admin Tester" : "Test Customer",
        role,
      };
      setUser(profile);
      return { ok: true, user: profile };
    }

    const users = readUsers();
    const found = users.find((x) => x.username === u && x.password === p);
    if (!found) return { ok: false, error: "Invalid username or password." };
    const profile = { username: found.username, name: found.name, role: found.role || "customer" };
    setUser(profile);
    return { ok: true, user: profile };
  }, []);

  const signup = useCallback(({ name, username, password, role = "customer" }) => {
    const n = (name || "").trim();
    const u = (username || "").trim();
    const p = (password || "").trim();
    if (!n || !u || !p) return { ok: false, error: "All fields are required." };
    if (u === "test") return { ok: false, error: "'test' is a reserved demo username." };
    const users = readUsers();
    if (users.some((x) => x.username === u))
      return { ok: false, error: "Username already exists." };
    users.push({ name: n, username: u, password: p, role });
    writeUsers(users);
    const profile = { username: u, name: n, role };
    setUser(profile);
    return { ok: true, user: profile };
  }, []);

  const logout = useCallback(() => setUser(null), []);

  return (
    <AuthContext.Provider value={{ user, login, signup, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
